// Qué imágenes trae un `DataTransfer` (pegar desde el portapapeles o soltar con
// drag and drop) — ADR-2026-09-19-pegar-y-soltar-imagenes-en-la-caja-de-prompt. Puro,
// para probarlo sin DOM real: recibe lo mínimo de la interfaz.

/** Lo mínimo de `DataTransfer` que se lee. */
export interface TransferLike {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<{ kind: string; type: string; getAsFile(): File | null }> | null;
  types?: ArrayLike<string> | null;
}

const EXT_IMAGEN = /\.(png|jpe?g|webp|gif|heic|heif|avif|bmp|tiff?)$/i;

/** Un archivo es imagen por su MIME o, si el navegador no lo puso (HEIC en algunos
 *  Chrome), por la extensión. */
export const esArchivoImagen = (f: File): boolean =>
  (f.type ? f.type.startsWith('image/') : false) || EXT_IMAGEN.test(f.name || '');

/**
 * Las imágenes del transfer, sin duplicar. El paste del portapapeles las trae en
 * `items` Y en `files` —la MISMA imagen, como dos `File` distintos—; el drop las trae en
 * las dos también. Se leen ambas fuentes y se deduplica por nombre + tamaño + tipo.
 *
 * Sin `lastModified` a propósito: en un paste cada `File` se crea en el momento y recibe
 * su propio `lastModified`, así que con él en la clave la misma captura se contaba dos
 * veces y se subía dos veces (bug 2026-09-19). Dos imágenes distintas con el mismo
 * nombre, el mismo peso en bytes y el mismo tipo son, a efectos prácticos, la misma.
 */
export function imagenesDeDataTransfer(dt: TransferLike | null | undefined): File[] {
  if (!dt) return [];
  const out: File[] = [];
  const vistas = new Set<string>();
  const sumar = (f: File | null | undefined) => {
    if (!f || !esArchivoImagen(f)) return;
    const clave = `${f.name}|${f.size}|${f.type}`;
    if (vistas.has(clave)) return;
    vistas.add(clave);
    out.push(f);
  };
  const items = dt.items ? Array.from(dt.items) : [];
  for (const it of items) if (it.kind === 'file') sumar(it.getAsFile());
  const files = dt.files ? Array.from(dt.files) : [];
  for (const f of files) sumar(f);
  return out;
}

/** ¿Lo que se está arrastrando son archivos? (En `dragover` los `files` vienen vacíos
 *  por seguridad; sólo `types` dice qué hay.) */
export function arrastraArchivos(dt: TransferLike | null | undefined): boolean {
  if (!dt?.types) return false;
  return Array.from(dt.types).includes('Files');
}
